import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getOfficeEducation } from '~/actions';
import type { TMissingOfficeInfoField } from '~/domain/OfficeMissingInfo';
import { useAppSelector } from '~/hooks/useAppSelector';

/** The software and insurance catalog, as stored names (untranslated). */
export interface IOfficeMissingInfoCatalog {
  softwares: string[];
  insurances: string[];
}

const CATALOG_FIELDS = ['softwares', 'insurances'] as const;

/**
 * The software / insurance catalog the pickers offer. It is fetched on demand, not at start-up,
 * and only when one of those fields is asked. The reducer stores the catalog's `DNT` entry, not
 * the typed response wrapper.
 *
 * `isReady` is false while an asked picker would offer nothing: the modal couldn't be saved, so
 * it stays closed until that list loads non-empty (and, if it never does, for this pass).
 */
export const useOfficeMissingInfoCatalog = (missingFields: TMissingOfficeInfoField[]) => {
  const dispatch = useDispatch();
  const officeEducation = useAppSelector(
    (state) =>
      state.user.officeEducation as
        | { softwares?: string[] | null; insurances?: string[] | null }
        | null
        | undefined,
  );
  const needsCatalog = CATALOG_FIELDS.some((field) => missingFields.includes(field));

  useEffect(() => {
    if (needsCatalog && !officeEducation) {
      dispatch(getOfficeEducation());
    }
  }, [needsCatalog, officeEducation, dispatch]);

  const catalog = useMemo<IOfficeMissingInfoCatalog>(
    () => ({
      softwares: officeEducation?.softwares ?? [],
      insurances: officeEducation?.insurances ?? [],
    }),
    [officeEducation?.softwares, officeEducation?.insurances],
  );

  const isReady = CATALOG_FIELDS.every(
    (field) => !missingFields.includes(field) || catalog[field].length > 0,
  );

  return { catalog, isReady };
};
